/**
 * API client for the Workspace system.
 * All calls use fetchWithAuth (credentials + Auth header).
 */

import { fetchWithAuth } from '@/lib/utils/api-auth';
import { extractApiError, extractErrorText, type ApiErrorParams } from '@/lib/api/shared';
import type {
  Workspace,
  WorkspaceMember,
  WorkspaceMembersResponse,
  WorkspaceInvite,
  WorkspaceRole,
  WorkspaceInviteStatus,
} from '@/types/shared/org';

export class ApiError extends Error {
  /** Stable snake_case backend code (auth_required, invite_admin_only, ...). */
  public readonly code: string;
  /** Interpolation params for the i18n key. */
  public readonly params?: ApiErrorParams;

  constructor(
    message: string,
    public readonly status: number,
    code?: string,
    params?: ApiErrorParams,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code ?? '';
    this.params = params;
  }
}

async function throwApiError(res: Response, prefix: string): Promise<never> {
  const err = await extractApiError(res);
  throw new ApiError(err.message ?? err.code ?? prefix, err.status, err.code, err.params);
}

// ---------------------------------------------------------------------------
// Orgs
// ---------------------------------------------------------------------------

export async function fetchOrgs(): Promise<Workspace[]> {
  const res = await fetchWithAuth('/api/workspaces');
  if (!res.ok) {
    throw new Error(`fetchOrgs failed: ${await extractErrorText(res)}`);
  }
  const json = (await res.json()) as { success: boolean; data: Workspace[] };
  return json.data;
}

export async function createOrg(input: { displayName: string; slug: string }): Promise<Workspace> {
  const res = await fetchWithAuth('/api/workspaces', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    await throwApiError(res, 'createOrg failed');
  }
  const json = (await res.json()) as { success: boolean; data: Workspace };
  return json.data;
}

export async function checkSlugAvailable(slug: string): Promise<boolean> {
  const res = await fetchWithAuth(`/api/workspaces/check-slug?slug=${encodeURIComponent(slug)}`);
  if (!res.ok) {
    throw new Error(`checkSlugAvailable failed: ${await extractErrorText(res)}`);
  }
  const json = (await res.json()) as { success: boolean; data: { available: boolean } };
  return json.data.available;
}

export async function updateOrg(
  orgId: string,
  input: {
    displayName?: string;
    avatarUrl?: string | null;
    slug?: string;
    // Per-invited-member monthly credit cap. 0 clears the limit (unlimited),
    // any positive integer enforces that many credits per YYYY-MM/member.
    // Owners are always unlimited regardless.
    defaultMemberMonthlyCreditLimit?: number;
  },
): Promise<Workspace> {
  const res = await fetchWithAuth(`/api/workspaces/${orgId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new Error(`updateOrg failed: ${await extractErrorText(res)}`);
  }
  const json = (await res.json()) as { success: boolean; data: Workspace };
  return json.data;
}

export async function deleteOrg(orgId: string): Promise<void> {
  const res = await fetchWithAuth(`/api/workspaces/${orgId}`, { method: 'DELETE' });
  if (!res.ok) {
    throw new Error(`deleteOrg failed: ${await extractErrorText(res)}`);
  }
}

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------

export async function fetchOrgMembers(orgId: string): Promise<WorkspaceMembersResponse> {
  const res = await fetchWithAuth(`/api/workspaces/${orgId}/members`);
  if (!res.ok) {
    throw new Error(`fetchOrgMembers failed: ${await extractErrorText(res)}`);
  }
  const json = (await res.json()) as { success: boolean; data: WorkspaceMembersResponse };
  return json.data;
}

export async function updateOrgMemberRole(
  orgId: string,
  userId: string,
  role: WorkspaceRole,
): Promise<WorkspaceMember> {
  const res = await fetchWithAuth(`/api/workspaces/${orgId}/members/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  if (!res.ok) {
    throw new Error(`updateOrgMemberRole failed: ${await extractErrorText(res)}`);
  }
  const json = (await res.json()) as { success: boolean; data: WorkspaceMember };
  return json.data;
}

export async function removeOrgMember(orgId: string, userId: string): Promise<void> {
  const res = await fetchWithAuth(`/api/workspaces/${orgId}/members/${userId}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error(`removeOrgMember failed: ${await extractErrorText(res)}`);
  }
}

// ---------------------------------------------------------------------------
// Invites
// ---------------------------------------------------------------------------

export interface CreateOrgInviteResult {
  invite: WorkspaceInvite;
  emailSent: boolean;
}

export async function createOrgInvite(
  orgId: string,
  email: string,
  role: WorkspaceRole,
): Promise<CreateOrgInviteResult> {
  const res = await fetchWithAuth(`/api/workspaces/${orgId}/invites`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role }),
  });
  if (!res.ok) {
    await throwApiError(res, 'createOrgInvite failed');
  }
  // Servers now emit emailSent; keep email_sent read-path for rollout compat.
  const json = (await res.json()) as {
    success: boolean;
    data: WorkspaceInvite;
    emailSent?: boolean;
    email_sent?: boolean;
  };
  const sent = json.emailSent ?? json.email_sent;
  return { invite: json.data, emailSent: sent !== false };
}

export async function revokeOrgInvite(orgId: string, inviteId: string): Promise<void> {
  const res = await fetchWithAuth(`/api/workspaces/${orgId}/invites/${inviteId}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error(`revokeOrgInvite failed: ${await extractErrorText(res)}`);
  }
}

export async function resendOrgInviteEmail(orgId: string, inviteId: string): Promise<{ emailSent: boolean }> {
  const res = await fetchWithAuth(`/api/workspaces/${orgId}/invites/${inviteId}/resend`, {
    method: 'POST',
  });
  if (!res.ok) {
    throw new Error(`resendOrgInviteEmail failed: ${await extractErrorText(res)}`);
  }
  // Servers now emit emailSent; keep email_sent read-path for rollout compat.
  const json = (await res.json()) as {
    success: boolean;
    emailSent?: boolean;
    email_sent?: boolean;
  };
  const sent = json.emailSent ?? json.email_sent;
  return { emailSent: sent !== false };
}

export async function acceptOrgInvite(token: string): Promise<Workspace> {
  const res = await fetchWithAuth('/api/org-invites/accept', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    throw new Error(`acceptOrgInvite failed: ${await extractErrorText(res)}`);
  }
  const json = (await res.json()) as { success: boolean; data: Workspace };
  return json.data;
}

export async function declineOrgInvite(token: string): Promise<void> {
  const res = await fetchWithAuth(`/api/org-invites/${token}/decline`, {
    method: 'PATCH',
  });
  if (!res.ok) {
    throw new Error(`declineOrgInvite failed: ${await extractErrorText(res)}`);
  }
}

// ---------------------------------------------------------------------------
// Billing
// ---------------------------------------------------------------------------

export async function createOrgBillingCheckout(
  orgId: string,
  plan: string,
  interval: 'MONTHLY' | 'YEARLY',
): Promise<{ checkoutUrl: string }> {
  const res = await fetchWithAuth(`/api/workspaces/${orgId}/billing/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan, interval }),
  });
  if (!res.ok) {
    throw new Error(`createOrgBillingCheckout failed: ${await extractErrorText(res)}`);
  }
  const json = (await res.json()) as { success: boolean; data: { checkoutUrl: string } };
  return json.data;
}

export async function getOrgInviteInfo(token: string): Promise<{
  orgName: string;
  inviterName: string;
  inviterEmail: string;
  role: WorkspaceRole;
  expiresAt: string;
  status: WorkspaceInviteStatus;
}> {
  const res = await fetchWithAuth(`/api/org-invites/${token}/info`);
  if (!res.ok) {
    throw new Error(`getOrgInviteInfo failed: ${await extractErrorText(res)}`);
  }
  const json = (await res.json()) as {
    success: boolean;
    data: {
      orgName: string;
      inviterName: string;
      inviterEmail: string;
      role: WorkspaceRole;
      expiresAt: string;
      status: WorkspaceInviteStatus;
    };
  };
  return json.data;
}
